'use client';

import { useStore } from '@/providers/app-store-provider';
import { isEmpty, isInZeloolApp } from '@/utils/tools';
import { useEffect } from 'react';
import InstadeskPop from './InstadeskPop';
import WebOnly from '@/components/Responsive/WebOnly';
import { CloseOutlined } from '@zeelool/icon';
import { createPortal } from 'react-dom';
import { env } from '@/utils/env';
/**
 * 加载 Zendesk 脚本
 */
function loadZendesk() {
  const oScript = document.createElement('script');
  oScript.type = 'text/javascript';
  oScript.id = 'ze-snippet';
  oScript.async = !0;
  oScript.src = `https://static.zdassets.com/ekr/snippet.js?key=${env.NEXT_PUBLIC_ZENDESK_KEY}`;
  // app中不加载客服插件
  if (!isInZeloolApp()) {
    document.head.appendChild(oScript);
    // 客服插件加载完成后，再执行打招呼配置数据的获取，防止点击客服气泡无效
    oScript.onload = () => {
      return Promise.resolve();
    };
  }
}

/**
 * 加载 Shulex 脚本
 */
function loadShulex(id: string, token: string, onLoad?: () => void) {
  const sc = document.createElement('script');
  sc.src = `https://apps.voc.ai/api_v2/gpt/bots/livechat/embed.js?id=${id}&token=${token}&hiddenSatisfaction=true`;
  sc.async = true;
  sc.defer = true;
  document.body.appendChild(sc);
  sc.onload = () => {
    onLoad?.();
  };
}

/**
 * 切换 Shulex 显示状态
 */
function toggleShulex(show: boolean) {
  const chatbotElement = document.querySelector('shulex-chatbot-lancher') as HTMLElement;
  if (chatbotElement) {
    chatbotElement.style.display = show ? 'block' : 'none';
  }
  if (chatbotElement && chatbotElement.shadowRoot) {
    // 创建style标签，写入要修改的样式
    const style = document.createElement('style');
    style.innerHTML = `
      #livechat_launcher_btn {
        display: none;
      }
    `;

    // 将style标签插入Shadow DOM内部
    chatbotElement.shadowRoot.appendChild(style);
  }
}

/**
 * 客服弹窗组件
 *
 * 在 layout.tsx 中使用，统一管理全局客服弹窗
 * - 负责初始化客服配置（加载脚本等）
 * - 负责展示和隐藏弹窗
 * - 状态通过全局 store 管理
 */
export default function CustomerServicePopups() {
  const publicResourceData = useStore((state) => state.publicResourceData);
  const isMobile = useStore((state) => state.isMobileScreen);

  // 从全局 store 读取状态
  const customerService = useStore((state) => state.customerService);
  const setCustomerService = useStore((state) => state.setCustomerService);

  /**
   * 初始化客服配置
   */
  useEffect(() => {
    const initData = async () => {
      if (!publicResourceData?.jsonvalues) {
        return;
      }

      const customerServiceConfig = {
        instadesk_chat: publicResourceData.jsonvalues.instadesk_chat,
        prescription_recognition_open: publicResourceData.jsonvalues.prescription_recognition_open,
        customer_service_popup: publicResourceData.jsonvalues.customer_service_popup,
      };

      const {
        enable_instadesk_pc,
        enable_instadesk_m,
        instadesk_url = '',
        id,
        token,
        desk_type,
      } = customerServiceConfig.instadesk_chat;

      const isEnableInstadesk = isMobile ? enable_instadesk_m : enable_instadesk_pc;
      const finalDeskType = isEnableInstadesk ? 'instadesk' : 'zendesk';

      setCustomerService({ deskType: finalDeskType });

      // 处理 Shulex
      if (desk_type && id && token && desk_type === 'shulex') {
        setCustomerService({ deskType: 'shulex' });
        await loadShulex(id, token, () => {
          toggleShulex(false);
        });
        return;
      }

      setCustomerService({ instadeskUrl: instadesk_url });

      // 处理 Zendesk 弹窗配置（如果需要）
      if (!isEmpty(customerServiceConfig.customer_service_popup)) {
        if (finalDeskType === 'zendesk') {
          await loadZendesk();
        }
      }
    };

    initData();
  }, [publicResourceData, isMobile, setCustomerService]);

  /**
   * 关闭 Shulex
   */
  const closeShulex = () => {
    toggleShulex(false);
    setCustomerService({ showShulexCloseIcon: false });
    if (window.__shulexLiveChat) {
      window.__shulexLiveChat.close();
    }
  };

  return (
    <>
      {/* Instadesk 弹窗 */}
      <InstadeskPop
        show={customerService.showInstadesk}
        instadeskUrl={customerService.instadeskUrl}
        onClose={() => setCustomerService({ showInstadesk: false })}
      />
      {/* Shulex 关闭按钮 - 挂载到 body */}
      {typeof window !== 'undefined' &&
        createPortal(
          <WebOnly>
            {customerService.deskType === 'shulex' && customerService.showShulexCloseIcon && (
              <button
                className="w-[50px] h-[50px] bg-primary-purple text-white text-[20px] rounded-full flex items-center justify-center fixed bottom-[10px] right-[10px] cursor-pointer z-[451]"
                onClick={closeShulex}
                aria-label="关闭客服"
              >
                <CloseOutlined className="text-[20px] text-white" />
              </button>
            )}
          </WebOnly>,
          document.body,
        )}
    </>
  );
}
